import {
  ChangeRequestContact,
  Contact,
  RequestingCompany,
  RequestingCompanyResponse,
} from "../types";
import { ChangeRequestFormValues } from "./formValue";
import { ewrDocumentTypes, mapFiles, coesDocumentTypes } from "../helper";
import { SectionVisibility } from "src/context/ChangeRequestContext";
import { normaliseDate } from "src/api/apiHelper";

export enum ChangeElectricityJobType {
  INSTALL_SOLAR = "INSTALL_SOLAR",
  ADD_OFF_PEAK = "ADD_OFF_PEAK",
  REMOVE_OFF_PEAK = "REMOVE_OFF_PEAK",
  MAINS_UPGRADE = "MAINS_UPGRADE",
  PHASE_UPGRADE = "PHASE_UPGRADE",
  MOVE_METER_POSITION = "MOVE_METER_POSITION",
  MOVE_TO_GROUP_METERING = "MOVE_TO_GROUP_METERING",
  MOVE_POINT_OF_ATTACHMENT = "MOVE_POINT_OF_ATTACHMENT",
  OVERHEAD_TO_UNDERGROUND_CONVERSION = "OVERHEAD_TO_UNDERGROUND_CONVERSION",
  REPLACE_METER_BOARD = "REPLACE_METER_BOARD",
}

export type FieldVisibility = {
  showMeterProvider: boolean;
  showOffPeakRequirements: boolean;
  showCoordinationOfElectricalWorks: boolean;
  showRexId: boolean;
  showCoesReferenceNumber: boolean;
  showCoesFiles: boolean;
  showEwrFiles: boolean;
  showSolarInstallationDetails: boolean;
  showInstallationDate: boolean;
  showSupplyType: boolean;
};

export const mapChangeTypeToShow = ({
  state,
  changeType,
}: {
  state: string;
  changeType: ChangeElectricityJobType;
}): FieldVisibility => {
  if (changeType === ChangeElectricityJobType.INSTALL_SOLAR) {
    return {
      showMeterProvider: ["ACT", "SA", "NSW"].includes(state),
      showOffPeakRequirements: false,
      showCoordinationOfElectricalWorks: state !== "VIC",
      showRexId: false,
      showCoesReferenceNumber: state === "VIC",
      showCoesFiles: state === "VIC",
      showEwrFiles: state === "VIC",
      showSolarInstallationDetails: true,
      showInstallationDate: true,
      showSupplyType: false,
    };
  }
  if (changeType === ChangeElectricityJobType.ADD_OFF_PEAK) {
    return {
      showMeterProvider: ["ACT", "SA", "NSW"].includes(state),
      showOffPeakRequirements: true,
      showCoordinationOfElectricalWorks: state !== "VIC",
      showRexId: false,
      showCoesReferenceNumber: state === "VIC",
      showCoesFiles: state === "VIC",
      showEwrFiles: state === "VIC",
      showSolarInstallationDetails: false,
      showInstallationDate: false,
      showSupplyType: true,
    };
  }
  //for all other change types
  return {
    showMeterProvider: ["ACT", "SA", "NSW"].includes(state),
    showOffPeakRequirements: false,
    showCoordinationOfElectricalWorks: state !== "VIC",
    showRexId: false,
    showCoesReferenceNumber: state === "VIC",
    showCoesFiles: state === "VIC",
    showEwrFiles: state === "VIC",
    showSolarInstallationDetails: false,
    showInstallationDate: false,
    showSupplyType: true,
  };
};

export const getShowFieldsForChangeTypes = ({
  state,
  changeTypes,
}: {
  state: string;
  changeTypes: ChangeElectricityJobType[];
}): FieldVisibility => {
  return changeTypes.reduce<FieldVisibility>(
    (acc, changeType) => {
      const currentTypeShows = mapChangeTypeToShow({ state, changeType });
      return {
        showMeterProvider:
          acc.showMeterProvider || currentTypeShows.showMeterProvider,
        showOffPeakRequirements:
          acc.showOffPeakRequirements ||
          currentTypeShows.showOffPeakRequirements,
        showCoordinationOfElectricalWorks:
          acc.showCoordinationOfElectricalWorks ||
          currentTypeShows.showCoordinationOfElectricalWorks,
        showRexId: acc.showRexId || currentTypeShows.showRexId,
        showCoesReferenceNumber:
          acc.showCoesReferenceNumber ||
          currentTypeShows.showCoesReferenceNumber,
        showCoesFiles: acc.showCoesFiles || currentTypeShows.showCoesFiles,
        showEwrFiles: acc.showEwrFiles || currentTypeShows.showEwrFiles,
        showSolarInstallationDetails:
          acc.showSolarInstallationDetails ||
          currentTypeShows.showSolarInstallationDetails,
        showInstallationDate:
          acc.showInstallationDate || currentTypeShows.showInstallationDate,
        showSupplyType: acc.showSupplyType || currentTypeShows.showSupplyType,
      };
    },
    {
      showMeterProvider: false,
      showOffPeakRequirements: false,
      showCoordinationOfElectricalWorks: false,
      showRexId: false,
      showCoesReferenceNumber: false,
      showCoesFiles: false,
      showEwrFiles: false,
      showSolarInstallationDetails: false,
      showInstallationDate: false,
      showSupplyType: false,
    }
  );
};

export const typeOfChangesOptions: Array<{
  label: string;
  value: ChangeElectricityJobType;
}> = [
  { label: "Solar", value: ChangeElectricityJobType.INSTALL_SOLAR },
  { label: "Add off-peak", value: ChangeElectricityJobType.ADD_OFF_PEAK },
  { label: "Remove off-peak", value: ChangeElectricityJobType.REMOVE_OFF_PEAK },
  { label: "Mains upgrade", value: ChangeElectricityJobType.MAINS_UPGRADE },
  { label: "Phase upgrade", value: ChangeElectricityJobType.PHASE_UPGRADE },
  {
    label: "Move meter position",
    value: ChangeElectricityJobType.MOVE_METER_POSITION,
  },
  {
    label: "Move to group meter position",
    value: ChangeElectricityJobType.MOVE_TO_GROUP_METERING,
  },
  {
    label: "Move point of attachment",
    value: ChangeElectricityJobType.MOVE_POINT_OF_ATTACHMENT,
  },
  {
    label: "Overhead to underground conversion",
    value: ChangeElectricityJobType.OVERHEAD_TO_UNDERGROUND_CONVERSION,
  },
  {
    label: "Replace meterboard",
    value: ChangeElectricityJobType.REPLACE_METER_BOARD,
  },
];

export const getChangeTypeOptions = (state: string) =>
  state === "SA"
    ? typeOfChangesOptions.filter(
        (option) => option.value === ChangeElectricityJobType.INSTALL_SOLAR
      )
    : typeOfChangesOptions;

export const offPeakOptions = [
  { label: "Slab heating", value: "slab" },
  { label: "Hot water heating", value: "hot_water" },
  { label: "Pool heating", value: "pool" },
  { label: "Other", value: "other" },
];

export enum RequesterType {
  PROPERTY_OWNER = "PROPERTY_OWNER",
  TENANT = "TENANT",
  BUILDER = "BUILDER",
  ELECTRICIAN = "ELECTRICIAN",
  PLUMBER = "PLUMBER",
  OTHER = "OTHER",
}

export const relationshipOptions = [
  {
    id: "owner",
    label: "Property owner",
    value: RequesterType.PROPERTY_OWNER,
  },
  { id: "tenant", label: "Tenant or resident", value: RequesterType.TENANT },
  {
    id: "builder-developer",
    label: "Builder or developer",
    value: RequesterType.BUILDER,
  },
  {
    id: "electrician",
    label: "Electrician",
    value: RequesterType.ELECTRICIAN,
  },
  { id: "plumber", label: "Plumber", value: RequesterType.PLUMBER },
  { id: "other", label: "Other", value: RequesterType.OTHER },
];

export const energyTypeOptions = [
  { id: "electricity", label: "Electricity", value: "electricity" },
  { id: "natural-gas", label: "Natural gas", value: "naturalGas" },
  { id: "lpg", label: "LPG", value: "lpg" },
];

export const mapToLabel = (
  value: string,
  options: Array<{ value: string; label: string }>
) => {
  return options.find((option) => option.value === value)?.label;
};

export const mapContactToChangeRequestContact: (
  contact: Contact | undefined
) => ChangeRequestContact = (contact) => {
  return {
    id: contact?.id ?? "",
    firstName: contact?.first_name ?? "",
    lastName: contact?.last_name ?? "",
    phoneNumber: contact?.phone_number ?? "",
    email: contact?.email ?? "",
    licenceNumber: contact?.license_number ?? "",
    contactType: "ELECTRICIAN",
  };
};

export const mapElectricianFieldsToChangeRequestContact: (
  values: ChangeRequestFormValues
) => ChangeRequestContact = (values) => {
  return {
    firstName: values.electricianFields.firstName ?? "",
    lastName: values.electricianFields.lastName ?? "",
    companyName: values.electricianFields.companyName ?? "",
    phoneNumber: values.electricianFields.phoneNumber ?? "",
    email: values.electricianFields.emailAddress ?? "",
    licenceNumber: values.electricianFields.licenceNumber ?? "",
    contactType: "ELECTRICIAN",
  };
};

export const mapFormikValuesToDraftService = (
  values: ChangeRequestFormValues
) => {
  const chosenElectricianDetails = values.licensedElectricians?.find(
    (e) => e?.id === values.licensedElectrician
  );

  const licensedElectricianDetails = values.noCompany
    ? mapElectricianFieldsToChangeRequestContact(values)
    : mapContactToChangeRequestContact(chosenElectricianDetails);

  return {
    requestor: values.relationship,
    jobTypes: values.changeTypes,
    licensedElectricianDetails,
    maxLoad: values.supplyDetails.maxLoad,
    requiredSupplyPhases: values.supplyDetails.numberOfPhases,
    installationType: values.supplyDetails.supplyType,
    meterProvider: values.meteringRequirements.meterProvider,
    offpeakRequirements: values.meteringRequirements.offPeakRequirements,
    otherOffpeakRequirement:
      values.meteringRequirements.otherOffPeakRequirements,
    locationDescription: values.meterAccess.meterLocation,
    solarInstallationDate: normaliseDate(
      values.solarInstallationDetails.installationDate,
      null
    ),
    rexId: values.supportingDocuments.rexId,
    coES: values.supportingDocuments.coesReferenceNumber,
    requiredCtMetering: values.supplyDetails.transformer,
    hasUnrestrainedAnimals: mapYesOrNoToBoolean(values.meterAccess.dogOnSite),
    isMeterLocked: mapYesOrNoToBoolean(values.meterAccess.meterInsideProperty),
    isMeterAccessLocked: mapYesOrNoToBoolean(
      values.meterAccess.meterBehindGate
    ),
    isCoordinationRequired: mapYesOrNoToBoolean(
      values.meterAccess.coordinationOfElectricalWorks
    ),
    // TODO: Add other fields
    accessInstructions: values.meterAccess.accessInstructions,
    accessDetails: getAccessDetails(
      values.meterAccess.meterLocation,
      values.meterAccess.accessInstructions
    ),
  };
};

export const getAccessDetails = (
  meterLocation: string,
  accessInstructions: string
) => {
  if (!meterLocation && !accessInstructions) return null;
  return meterLocation && accessInstructions
    ? `${meterLocation}, ${accessInstructions}`
    : meterLocation || accessInstructions;
};

export type ChangeElectricityServiceAfterSubmission = {
  requestor?: RequesterType;
  jobTypes?: Array<ChangeElectricityJobType>;
  licensedElectricianDetails?: ChangeRequestContact;
  maxLoad?: string;
  requiredSupplyPhases?: string;
  installationType?: string;
  meterProvider?: string;
  offpeakRequirements?: Array<string>;
  otherOffpeakRequirement?: string;
  locationDescription?: string;
  solarInstallationDate?: string;
  rexId?: string;
  coes?: string;

  requiredCtMetering?: boolean;
  isMeterAccessLocked?: boolean;
  isMeterLocked?: boolean;
  hasUnrestrainedAnimals?: boolean;
  isCoordinationRequired?: boolean;

  accessInstructions?: string;
  accessDetails?: string;
};

const mapYesOrNoToBoolean = (yesOrNo: "yes" | "no" | null): boolean | null => {
  if (yesOrNo === "yes") {
    return true;
  }
  if (yesOrNo === "no" || yesOrNo === null) {
    return false;
  }
  return false;
};

export const mapDraftServiceToFormikValues = (
  data: any
): ChangeRequestFormValues => {
  const {
    services: [service],
    premise,
  } = data;

  const draftPayload = JSON.parse(
    service.payload
  ) as ChangeElectricityServiceAfterSubmission;

  const coesFiles = mapFiles(service.attachments_meta ?? [], coesDocumentTypes);
  const ewrFiles = mapFiles(service.attachments_meta ?? [], ewrDocumentTypes);

  return {
    requester: service?.requestor,
    submittedDateTime: service?.submitted_date_time
      ? new Date(service.submitted_date_time)
      : undefined,
    // Mappable fields
    coesFiles,
    ewrFiles,
    nmi: service.service_identifier ?? "",
    mirn: service.service_identifier ?? "",
    requestingCompany: premise?.site_details?.requesting_company
      ? mapRequestingCompanyResponseToRequestingCompany(
          premise.site_details.requesting_company
        )
      : null,
    noCompany: !premise?.site_details?.requesting_company,
    premise,
    energyType: "electricity",
    relationship: draftPayload.requestor ?? RequesterType.PROPERTY_OWNER,
    changeTypes: draftPayload.jobTypes ?? [],
    licensedElectrician: draftPayload.licensedElectricianDetails?.id ?? null,

    electricianFields: {
      firstName: draftPayload.licensedElectricianDetails?.firstName ?? null,
      lastName: draftPayload.licensedElectricianDetails?.lastName ?? null,
      companyName: draftPayload.licensedElectricianDetails?.companyName ?? null,
      phoneNumber: draftPayload.licensedElectricianDetails?.phoneNumber ?? null,
      emailAddress: draftPayload.licensedElectricianDetails?.email ?? null,
      licenceNumber:
        draftPayload.licensedElectricianDetails?.licenceNumber ?? null,
    },

    meteringRequirements: {
      meterProvider: draftPayload.meterProvider ?? "origin",
      offPeakRequirements: draftPayload.offpeakRequirements ?? [],
      otherOffPeakRequirements: draftPayload.otherOffpeakRequirement ?? null,
    },

    supplyDetails: {
      maxLoad: draftPayload.maxLoad ?? "",
      numberOfPhases: draftPayload.requiredSupplyPhases ?? "1",
      supplyType: draftPayload.installationType ?? "overhead",
      offPeakRequirements: draftPayload.offpeakRequirements ?? [],
      transformer: draftPayload.requiredCtMetering ?? false,
    },

    meterAccess: {
      meterLocation: draftPayload.locationDescription ?? "",
      accessDetails: draftPayload.accessDetails ?? "",
      accessInstructions: draftPayload.accessInstructions ?? "",
      meterBehindGate:
        draftPayload.isMeterAccessLocked === undefined
          ? null
          : draftPayload.isMeterAccessLocked
          ? "yes"
          : "no",
      meterInsideProperty:
        draftPayload.isMeterLocked === undefined
          ? null
          : draftPayload.isMeterLocked
          ? "yes"
          : "no",
      dogOnSite:
        draftPayload.hasUnrestrainedAnimals === undefined
          ? null
          : draftPayload.hasUnrestrainedAnimals
          ? "yes"
          : "no",
      coordinationOfElectricalWorks:
        draftPayload.isCoordinationRequired === undefined
          ? null
          : draftPayload.isCoordinationRequired
          ? "yes"
          : "no",
    },

    solarInstallationDetails: {
      installationDate: draftPayload.solarInstallationDate
        ? new Date(draftPayload.solarInstallationDate)
        : null,
    },

    supportingDocuments: {
      rexId: draftPayload.rexId ?? null,
      coesReferenceNumber: draftPayload.coes ?? null,
    },

    supplyServiceOrderNumber: service.supply_service_order_number ?? null,
    networkFormNumber: service.network_form_number ?? null,
    requesterName: service.requestor ?? null,
    requesterEmail: service.requestor_email ?? null,
    channel: service.channel ?? null,
  };
};

export const getFullName = (firstName: string, lastName: string): string => {
  if (!firstName && !lastName) return "";
  return `${firstName} ${lastName}`.trim();
};

export const getPhaseLabel = (value: string): string => {
  const phaseMap: Record<string, string> = {
    "1": "Single phase",
    "2": "Two phase",
    "3": "Three phase",
  };
  return phaseMap[value] || "";
};

export const meterProviderOptions = {
  origin: "Origin",
  intellihub: "IntelliHub",
  blueCurrent: "BlueCurrent",
} as const;

export const mapPostSubmissionMeterProviderToLabel = (
  meterProvider: string
): string => {
  switch (meterProvider) {
    case "ACUMENMP":
      return "IntelliHub";
    case "VECTORMP":
      return "BlueCurrent";
    case "TCAMP":
      return "Plus ES";
    default:
      return "Origin";
  }
};

export const getMeterProviderLabel = (
  value: keyof typeof meterProviderOptions
): string => {
  return meterProviderOptions[value] || "";
};

export const getElectricitySections = ({
  isSubmitted,
  sectionVisibility,
}: {
  isSubmitted: boolean;
  sectionVisibility: SectionVisibility;
}) => [
  {
    name: "Premises",
    id: "premiseReadonlySection",
    isVisible: true,
  },
  {
    name: "Change type",
    id: "changeTypeSection",
    isVisible: true,
  },
  {
    name: "Licensed electrician",
    id: "electricianSection",
    isVisible: true,
  },
  {
    name: "Supply details",
    id: "supplyDetailsSection",
    isVisible: true,
  },
  {
    name: "Metering requirements",
    id: "meteringRequirementsSection",
    isVisible: sectionVisibility?.meteringRequirementsSection,
  },
  {
    name: "Meter access",
    id: "meterAccessSection",
    isVisible: true,
  },
  {
    name: "Solar installation details",
    id: "solarInstallationDetailsSection",
    isVisible: sectionVisibility?.solarInstallationDetailsSection,
  },
  {
    name: "Supporting documents",
    id: "supportingDocumentsSection",
    isVisible: sectionVisibility?.supportingDocumentsSection,
  },
  {
    name: "Fees, process and timings",
    id: "feesProcessAndTimingsSection",
    isVisible: !isSubmitted,
  },
];

export const mapFieldVisibilityToFieldInfo: {
  [K in keyof FieldVisibility]: {
    fieldName: string;
    initValue: string | boolean | null | string[];
  };
} = {
  showMeterProvider: {
    fieldName: "meteringRequirements.meterProvider",
    initValue: "origin",
  },
  showOffPeakRequirements: {
    fieldName: "meteringRequirements.offPeakRequirements",
    initValue: [],
  },
  showCoordinationOfElectricalWorks: {
    fieldName: "meterAccess.coordinationOfElectricalWorks",
    initValue: "no",
  },
  showRexId: {
    fieldName: "supportingDocuments.rexId",
    initValue: null,
  },
  showCoesReferenceNumber: {
    fieldName: "supportingDocuments.coesReferenceNumber",
    initValue: null,
  },
  showCoesFiles: {
    fieldName: "coesFiles",
    initValue: [],
  },
  showEwrFiles: {
    fieldName: "ewrFiles",
    initValue: [],
  },
  showInstallationDate: {
    fieldName: "solarInstallationDetails.installationDate",
    initValue: null,
  },
  showSolarInstallationDetails: {
    fieldName: "invalid",
    initValue: null,
  },
  showSupplyType: {
    fieldName: "supplyDetails.supplyType",
    initValue: "overhead",
  },
};

export type FieldChange = {
  from: boolean;
  to: boolean;
};

export type FieldChanges = {
  [K in keyof FieldVisibility]?: FieldChange;
};

export const mapRequestingCompanyResponseToRequestingCompany = (
  response: RequestingCompanyResponse
): RequestingCompany => ({
  msaNumber: response.msa_number,
  id: response.id,
  name: response.name,
  operationalTeam: response.operational_team,
});

export const mapRequestingCompanyToResponse = (
  company: RequestingCompany
): RequestingCompanyResponse => ({
  msa_number: company.msaNumber,
  id: company.id,
  name: company.name,
  operational_team: company.operationalTeam,
});
